import { Add, Refresh, SearchNormal1 } from "iconsax-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import { ToastContext } from "../../component/global/ToastContext";
import PaginationComponent from "../../component/global/PaginationComponent";
import ShareContent from "../../pages/CustomerClub/Components/ContentManagment/ShareContent";
import ContentCampaignsPanel from "./ContentCampaignsPanel";
import ContentAnalytics from "./ContentAnalytics";
import ContentCard from "./ContentCard";
import ContentEditor from "./ContentEditor";
import { contentStudioApi, errorMessage } from "./api";
import {
    CONTENT_SOURCE,
    CONTENT_STATUS,
    CONTENT_TARGET,
    PAGE_SIZE,
    STUDIO_SECTIONS,
} from "./constants";
import { detectSourceFromUrl } from "./helpers";
import "./styles.scss";

const initialList = { rows: [], count: 0 };

const payloadFromDraft = (draft, intent) => {
    const payload = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        content_type: draft.contentType,
        source: draft.source,
        target_scope: draft.targetScope,
        intent,
    };
    if (draft.source === CONTENT_SOURCE.FILE) {
        if (draft.file) payload.file = draft.file;
    } else {
        payload.url = draft.url.trim();
        payload.source = detectSourceFromUrl(draft.url) || draft.source;
    }
    if (draft.targetScope === CONTENT_TARGET.SURVEY) {
        payload.survey = draft.surveyId;
    }
    if (draft.targetScope === CONTENT_TARGET.ITEM_TYPE) {
        payload.item_type = draft.itemTypeId;
    }
    if (draft.targetScope === CONTENT_TARGET.ITEM) {
        payload.item_type = draft.itemTypeId;
        payload.item_id = draft.itemId;
    }
    return payload;
};

export default function ContentStudio() {
    const { t, i18n } = useTranslation();
    const showToast = useContext(ToastContext);
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";
    const [section, setSection] = useState("library");
    const [list, setList] = useState(initialList);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [error, setError] = useState("");
    const [analyticsError, setAnalyticsError] = useState("");
    const [filters, setFilters] = useState({
        search: "",
        status: "",
        content_type: "",
        target_scope: "",
    });
    const [page, setPage] = useState(0);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editorError, setEditorError] = useState("");
    const [shareTarget, setShareTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const pageCount = Math.max(1, Math.ceil((list.count || 0) / PAGE_SIZE));
    const totals = analytics?.totals || {};
    const pipeline = useMemo(() => {
        return [
            { key: "", label: t("contentStudio.pipeline.all"), count: totals.contents ?? list.count },
            {
                key: String(CONTENT_STATUS.DRAFT),
                label: t("contentStudio.status.draft"),
                count: totals.draft ?? 0,
            },
            {
                key: String(CONTENT_STATUS.IN_REVIEW),
                label: t("contentStudio.status.pending"),
                count: totals.pending ?? 0,
            },
            {
                key: String(CONTENT_STATUS.CONFIRMED),
                label: t("contentStudio.status.published"),
                count: totals.published ?? 0,
            },
            {
                key: String(CONTENT_STATUS.REJECTED),
                label: t("contentStudio.status.rejected"),
                count: totals.rejected ?? 0,
            },
        ];
    }, [analytics, list.count, t]);

    const loadList = useCallback(() => {
        setLoading(true);
        setError("");
        const params = {
            page: page + 1,
            page_size: PAGE_SIZE,
        };
        if (filters.search) params.name = filters.search;
        if (filters.status) params.status = filters.status;
        if (filters.content_type) params.content_type = filters.content_type;
        if (filters.target_scope) params.target_scope = filters.target_scope;
        contentStudioApi
            .list(params)
            .then(setList)
            .catch((requestError) =>
                setError(errorMessage(requestError, t("contentStudio.errors.load")))
            )
            .finally(() => setLoading(false));
    }, [filters, page, t]);

    const loadAnalytics = useCallback(() => {
        setAnalyticsLoading(true);
        setAnalyticsError("");
        contentStudioApi
            .analytics()
            .then(setAnalytics)
            .catch((requestError) =>
                setAnalyticsError(
                    errorMessage(requestError, t("contentStudio.errors.load"))
                )
            )
            .finally(() => setAnalyticsLoading(false));
    }, [t]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    useEffect(() => {
        if (section === "library") loadList();
        if (section === "analytics") loadAnalytics();
    }, [section, loadList, loadAnalytics]);

    const openCreate = () => {
        setEditing(null);
        setEditorError("");
        setUploadProgress(0);
        setEditorOpen(true);
    };
    const openEdit = (content) => {
        setEditing(content);
        setEditorError("");
        setUploadProgress(0);
        setEditorOpen(true);
    };
    const closeEditor = () => {
        if (saving) return;
        setEditorOpen(false);
        setEditing(null);
        setEditorError("");
    };

    const save = (draft, intent) => {
        setSaving(true);
        setEditorError("");
        const payload = payloadFromDraft(draft, intent);
        const request = editing?.id
            ? contentStudioApi.update(editing.id, payload, (event) => {
                  if (event.total) {
                      setUploadProgress(
                          Math.round((event.loaded * 100) / event.total)
                      );
                  }
              })
            : contentStudioApi.create(payload, (event) => {
                  if (event.total) {
                      setUploadProgress(
                          Math.round((event.loaded * 100) / event.total)
                      );
                  }
              });
        request
            .then((saved) => {
                const stillDraft =
                    intent === "review" &&
                    saved?.id &&
                    (saved.status === CONTENT_STATUS.DRAFT ||
                        saved.status === CONTENT_STATUS.REJECTED);
                if (!stillDraft) return saved;
                return contentStudioApi.submit(saved.id);
            })
            .then(() => {
                showToast(
                    t(
                        intent === "draft"
                            ? "contentStudio.toast.draft"
                            : "contentStudio.toast.submitted"
                    ),
                    "success"
                );
                setEditorError("");
                setEditorOpen(false);
                setEditing(null);
                loadList();
                loadAnalytics();
            })
            .catch((requestError) =>
                setEditorError(
                    errorMessage(requestError, t("contentStudio.errors.save"))
                )
            )
            .finally(() => {
                setSaving(false);
                setUploadProgress(0);
            });
    };

    const submitExisting = (content) => {
        contentStudioApi
            .submit(content.id)
            .then(() => {
                showToast(t("contentStudio.toast.submitted"), "success");
                loadList();
                loadAnalytics();
            })
            .catch((requestError) =>
                showToast(
                    errorMessage(requestError, t("contentStudio.errors.save")),
                    "danger"
                )
            );
    };

    const duplicate = (content) => {
        contentStudioApi
            .duplicate(content.id)
            .then(() => {
                showToast(t("contentStudio.toast.duplicated"), "success");
                loadList();
                loadAnalytics();
            })
            .catch((requestError) =>
                showToast(
                    errorMessage(requestError, t("contentStudio.errors.save")),
                    "danger"
                )
            );
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        contentStudioApi
            .remove(deleteTarget.id)
            .then(() => {
                showToast(t("contentStudio.toast.deleted"), "success");
                setDeleteTarget(null);
                loadList();
                loadAnalytics();
            })
            .catch((requestError) =>
                showToast(
                    errorMessage(requestError, t("contentStudio.errors.delete")),
                    "danger"
                )
            )
            .finally(() => setDeleting(false));
    };

    return (
        <div className="content-studio">
            <header className="studio-header">
                <div>
                    <h1>{t("contentStudio.title")}</h1>
                    <span>{t("contentStudio.subtitle")}</span>
                </div>
                {section === "library" ? (
                    <DBButton
                        type="primary"
                        caption={
                            <>
                                <Add size={18} aria-hidden="true" />
                                {t("contentStudio.create")}
                            </>
                        }
                        onClick={openCreate}
                    />
                ) : null}
            </header>

            <nav className="studio-section-tabs" aria-label={t("contentStudio.sections")}>
                {STUDIO_SECTIONS.map((item) => (
                    <button
                        key={item}
                        type="button"
                        aria-current={section === item ? "page" : undefined}
                        onClick={() => setSection(item)}
                    >
                        {t(`contentStudio.section.${item}`)}
                    </button>
                ))}
            </nav>

            {section === "library" ? (
                <>
                    <div
                        className="studio-pipeline"
                        role="tablist"
                        aria-label={t("contentStudio.pipeline.label")}
                    >
                        {pipeline.map((item) => (
                            <button
                                key={item.key || "all"}
                                type="button"
                                role="tab"
                                aria-selected={filters.status === item.key}
                                onClick={() => {
                                    setPage(0);
                                    setFilters((current) => ({
                                        ...current,
                                        status: item.key,
                                    }));
                                }}
                            >
                                <strong>{item.count || 0}</strong>
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                    <form
                        className="studio-filters"
                        onSubmit={(event) => {
                            event.preventDefault();
                            setPage(0);
                            loadList();
                        }}
                    >
                        <label className="studio-search">
                            <SearchNormal1 size={18} aria-hidden="true" />
                            <input
                                type="search"
                                value={filters.search}
                                placeholder={t("contentStudio.filters.search")}
                                onChange={(event) =>
                                    setFilters((current) => ({
                                        ...current,
                                        search: event.target.value,
                                    }))
                                }
                            />
                        </label>
                        <select
                            value={filters.content_type}
                            aria-label={t("contentStudio.filters.type")}
                            onChange={(event) => {
                                setPage(0);
                                setFilters((current) => ({
                                    ...current,
                                    content_type: event.target.value,
                                }));
                            }}
                        >
                            <option value="">{t("contentStudio.filters.allTypes")}</option>
                            <option value="1">{t("contentStudio.type.video")}</option>
                            <option value="4">{t("contentStudio.type.audio")}</option>
                            <option value="3">{t("contentStudio.type.image")}</option>
                        </select>
                        <select
                            value={filters.target_scope}
                            aria-label={t("contentStudio.filters.target")}
                            onChange={(event) => {
                                setPage(0);
                                setFilters((current) => ({
                                    ...current,
                                    target_scope: event.target.value,
                                }));
                            }}
                        >
                            <option value="">
                                {t("contentStudio.filters.allTargets")}
                            </option>
                            {Object.values(CONTENT_TARGET).map((value) => (
                                <option key={value} value={value}>
                                    {t(`contentStudio.target.${value}`)}
                                </option>
                            ))}
                        </select>
                        <button type="submit">{t("contentStudio.filters.apply")}</button>
                    </form>
                    {loading ? (
                        <div className="studio-state" role="status" aria-live="polite">
                            <span className="studio-spinner" aria-hidden="true" />
                            {t("contentStudio.loading")}
                        </div>
                    ) : error ? (
                        <div className="studio-state studio-state-error" role="alert">
                            <p>{error}</p>
                            <button type="button" onClick={loadList}>
                                <Refresh size={18} aria-hidden="true" />
                                {t("contentStudio.retry")}
                            </button>
                        </div>
                    ) : list.rows.length === 0 ? (
                        <div className="studio-empty">
                            <h2>{t("contentStudio.emptyTitle")}</h2>
                            <p>{t("contentStudio.empty")}</p>
                            <DBButton
                                caption={t("contentStudio.create")}
                                onClick={openCreate}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="studio-grid">
                                {list.rows.map((content) => (
                                    <ContentCard
                                        key={content.id}
                                        content={content}
                                        locale={locale}
                                        onEdit={openEdit}
                                        onShare={(item) => setShareTarget(item)}
                                        onDuplicate={duplicate}
                                        onDelete={setDeleteTarget}
                                        onSubmit={submitExisting}
                                    />
                                ))}
                            </div>
                            {pageCount > 1 ? (
                                <PaginationComponent
                                    pageCount={pageCount}
                                    currentPage={page}
                                    fetchData={({ pageIndex }) => setPage(pageIndex)}
                                    setCurrentPage={setPage}
                                />
                            ) : null}
                        </>
                    )}
                </>
            ) : section === "send" ? (
                <ContentCampaignsPanel />
            ) : (
                <ContentAnalytics
                    data={analytics}
                    locale={locale}
                    loading={analyticsLoading}
                    error={analyticsError}
                    onRetry={loadAnalytics}
                />
            )}

            <ContentEditor
                show={editorOpen}
                content={editing}
                saving={saving}
                uploadProgress={uploadProgress}
                error={editorError}
                onHide={closeEditor}
                onSave={save}
            />
            <Modal
                show={Boolean(shareTarget)}
                onHide={() => setShareTarget(null)}
                centered
            >
                <Modal.Body className="modal-body social-modal">
                    {shareTarget ? (
                        <ShareContent
                            onHide={() => setShareTarget(null)}
                            content={shareTarget.id}
                        />
                    ) : null}
                </Modal.Body>
            </Modal>
            <DBConfirmModal
                show={Boolean(deleteTarget)}
                onHide={() => setDeleteTarget(null)}
                title={t("contentStudio.deleteTitle")}
                description={t("contentStudio.deleteBody", {
                    name: deleteTarget?.name,
                })}
                btnTitle={t("contentStudio.delete")}
                loading={deleting}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
